import { createFeatureSelector, createSelector } from '@ngrx/store';
import { CopilotState } from './copilot.reducer';

export const selectCopilotState = createFeatureSelector<CopilotState>('copilot');

export const selectedClientId = createSelector(
  selectCopilotState,
  (state) => state.selectedClientId
);

export const selectedClient = createSelector(
  selectCopilotState,
  selectedClientId,
  (state, clientId) => clientId ? state.clients[clientId] : undefined
);

export const selectedConversationId = createSelector(
  selectedClient,
  (client) => client?.selectedConversationId ?? null
);

/** Selected conversation row from the client list, if loaded. */
export const selectSelectedConversation = createSelector(
  selectedClient,
  selectedConversationId,
  (client, conversationId) => {
    if (!client || !conversationId) {
      return null;
    }
    return client.conversations?.find((c) => c.id === conversationId) ?? null;
  }
);

export const selectMessagesForSelectedConversation = createSelector(
  selectedClient,
  (client) => {
    if (!client || !client.selectedConversationId) return [];
    return client.messages?.[client.selectedConversationId] ?? [];
  }
);

export const selectIsStreaming = (conversationId: string) =>
  createSelector(
    selectedClient,
    (client) => {
      const execution = client?.executionStatus?.[conversationId];
      return (
        client?.activeRuns?.[conversationId] !== undefined ||
        execution === 'queued' ||
        execution === 'running' ||
        execution === 'waiting_for_input'
      );
    }
  );

export const selectActiveRunForConversation = (conversationId: string) =>
  createSelector(
    selectedClient,
    (client) => client?.activeRuns?.[conversationId] ?? null
  );

export const selectAgentIdentifierForSelectedConversation = createSelector(
  selectedClient,
  (client) => {
    const conversationId = client?.selectedConversationId;
    return conversationId
      ? client.agentIdentifierByConversation?.[conversationId] ?? null
      : null;
  }
);

export const selectMessagesByConversation = (clientId: string, conversationId: string) =>
  createSelector(
    selectCopilotState,
    state => {
      const client = state.clients?.[clientId];
      const messages = client?.messages?.[conversationId];
      return Array.isArray(messages) ? messages : [];
    }
  );

export const selectHighlightedClientIds = createSelector(
  selectCopilotState,
  (state) => state.highlightedClientIds
);

export const selectInputTextForConversation = (clientId: string, conversationId: string) =>
  createSelector(
    selectCopilotState,
    (state) => {
      const client = state.clients?.[clientId];
      return client?.inputTextByConversation?.[conversationId] ?? '';
    }
  );

export const selectClientGuidForConversation = (
  clientId: string,
  conversationId: string
) =>
  createSelector(
    selectCopilotState,
    (state) =>
      state.clients?.[clientId]?.clientGuidByConversation?.[conversationId] ??
      null
  );

export const selectHighlightedConversations = createSelector(
  selectCopilotState,
  (state) => state.highlightedConversations
);

/** Query + active index for the selected conversation (defaults when none selected). */
export const selectChatSearchState = createSelector(
  selectedClient,
  (client) => {
    const convId = client?.selectedConversationId ?? null;
    if (!client || !convId) {
      return { query: '', activeMatchIndex: 0 };
    }
    const entry = client.chatSearchByConversation?.[convId];
    return entry ?? { query: '', activeMatchIndex: 0 };
  }
);
